import { ReactElement, useState } from 'react';
import {
  ControllerContainer, CurrentTemperatureEnvironmentContainer, ModeMachineContainer,
  SetpointTextLoading,
  VentilationContent,
} from './styles';
import { SetpointController } from './components/SetpointController';
import { EnvironmentTemperatureItem } from './components/EnvironmentTemperatureItem';
import { OperationButton } from './components/OperationButton';
import { useTranslation } from 'react-i18next';
import fanAnimation from '../../../assets/img/remoteControl/fanAnimation.gif';
import { EnvironmentItem, EnvironmentWithTemperature, Operation } from './types';
import { DEFAULT_SETPOINT_LIMITS } from './helpers';
import Skeleton from 'react-loading-skeleton';
import { ModalManualMode } from '~/components/ModalsRemoteControl/ManualMode';
import { ModalOnOffConfirmation } from '~/components/ModalsRemoteControl/OnOffConfirmation';
import { PowerButton } from './components/PowerButtonVentilation';

interface SetpointContentProps {
  devAutCode?: string;
  checkAutomaticMode: boolean;
  loading: boolean;
  currentOperation?: Operation;
  currentSetpoint?: number;
  limits?: { tMin: number; tMax: number };
  environments: EnvironmentItem[];
  onChangeSetpoint: (setpoint?: number) => void;
  onPowerChange: () => void;
  onClickModality: (modality: 'fan' | 'cool') => void;
  onClickTurnOffEconomyMode: () => void;
}

export function SetpointContent({
  devAutCode,
  checkAutomaticMode,
  loading,
  limits,
  currentOperation,
  currentSetpoint,
  environments,
  onChangeSetpoint,
  onClickModality,
  onPowerChange,
  onClickTurnOffEconomyMode,
}: Readonly<SetpointContentProps>): ReactElement {
  const [openManualModeModal, setOpenManualMode] = useState(false);
  const [openConfirmPowerModal, setOpenConfirmPowerModal] = useState(false);
  const { t } = useTranslation();

  const environmentsWithTemperature = environments.filter(
    (environment): environment is EnvironmentWithTemperature => !!environment.returnTemperatureField,
  );

  function onClickContainer(event): void {
    const isButtonClick = !!event.target.closest('button');
    if (checkAutomaticMode && isButtonClick) {
      setOpenManualMode(true);
    }
  }

  function onCloseManualModeModal(): void {
    setOpenManualMode(false);
  }

  function onClickPowerButton(): void {
    setOpenConfirmPowerModal(true);
  }

  function onCloseConfirmModal(): void {
    setOpenConfirmPowerModal(false);
  }

  function onConfirmPowerClick(): void {
    onCloseConfirmModal();
    onPowerChange();
  }

  function handleTurnOffEconomyMode() {
    setOpenManualMode(false);
    onClickTurnOffEconomyMode();
  }

  if (loading) {
    return (
      <>
        <SetpointTextLoading>
          <Skeleton height="30px" />
        </SetpointTextLoading>
        <ControllerContainer>
          <SetpointController
            loading
            disabled
            limits={{
              tMin: 0,
              tMax: 0,
            }}
            power={undefined}
            currentSetpoint={undefined}
            onClickPower={() => {}}
            onChangeSetpoint={() => {}}
          />
        </ControllerContainer>
        <CurrentTemperatureEnvironmentContainer>
          <SetpointTextLoading>
            <Skeleton height="16px" />
          </SetpointTextLoading>
        </CurrentTemperatureEnvironmentContainer>
        <ModeMachineContainer>
          <OperationButton
            loading
            onClickModality={() => {}}
            operation="fan"
            currentOperation="fan"
            disabled={false}
          />
          <OperationButton
            loading
            onClickModality={() => {}}
            operation="fan"
            currentOperation="fan"
            disabled={false}
          />
        </ModeMachineContainer>
      </>
    );
  }

  return (
    <>
      <ModalOnOffConfirmation
        isOpen={openConfirmPowerModal}
        onClose={onCloseConfirmModal}
        onConfirm={onConfirmPowerClick}
        status={currentOperation === 'disabled' ? 'on' : 'off'}
      />
      <ModalManualMode
        isOpen={openManualModeModal}
        onClose={onCloseManualModeModal}
        onTurnOffEconomyMode={handleTurnOffEconomyMode}
      />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h1>{t('temperaturaDesejada')}</h1>
      </div>
      <ControllerContainer onClick={onClickContainer}>
        {currentOperation === 'fan' && !checkAutomaticMode ? (
          <VentilationContent>
            <img src={fanAnimation} alt={t('ventilar')} style={{ height: '131px' }} loading="lazy" data-testid="fan-animation" />
            <PowerButton onClickModality={onClickPowerButton} loading={loading} />
          </VentilationContent>
        ) : (
          <SetpointController
            currentSetpoint={currentSetpoint}
            power={currentOperation ? currentOperation !== 'disabled' : undefined}
            limits={limits ?? DEFAULT_SETPOINT_LIMITS}
            disabled={loading || checkAutomaticMode}
            onChangeSetpoint={onChangeSetpoint}
            onClickPower={onClickPowerButton}
          />
        )}
        {environmentsWithTemperature.length > 0 && (
        <CurrentTemperatureEnvironmentContainer>
          <ul>
            {environmentsWithTemperature.map((env) => (
              <EnvironmentTemperatureItem
                deviceCode={env.selfReference && devAutCode ? devAutCode : env.dutCode}
                temperatureKey={env.returnTemperatureField}
                selfReference={env.selfReference}
                key={`${env.name}-${env.dutCode}`}
              />
            ))}
          </ul>
        </CurrentTemperatureEnvironmentContainer>
        )}
      </ControllerContainer>
      {currentOperation && (
      <ModeMachineContainer onClick={onClickContainer}>
        <OperationButton
          currentOperation={currentOperation}
          disabled={loading || checkAutomaticMode || currentOperation === 'disabled'}
          onClickModality={() => onClickModality('cool')}
          operation="cool"
        />
        <OperationButton
          currentOperation={currentOperation}
          disabled={loading || checkAutomaticMode || currentOperation === 'disabled'}
          onClickModality={() => onClickModality('fan')}
          operation="fan"
        />
      </ModeMachineContainer>
      )}
    </>
  );
}
